<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import { getGlobalSettings } from "../getGlobalSettings";
	import type { ServiceProps } from "./ServiceProps";
	import integrationHero from "../assets/images/integration/integration-hero.png";
	import integrationHeroSm from "../assets/images/integration/integration-hero.png?webp&h=180&w=608&imagetools";
	import integrationHeroMd from "../assets/images/integration/integration-hero.png?webp&h=207&w=704&imagetools";
	import integrationHeroLg from "../assets/images/integration/integration-hero.png?webp&h=285&w=960&imagetools";
	import integrationHeroXl from "../assets/images/integration/integration-hero.png?webp&h=420&w=1424&imagetools";
	import integration1 from "../assets/images/integration/integration1.png";
	import integration1Sm from "../assets/images/integration/integration1.png?webp&h=135&w=300&imagetools";
	import integration1Md from "../assets/images/integration/integration1.png?webp&h=160&w=348&imagetools";
	import integration1Lg from "../assets/images/integration/integration1.png?webp&h=215&w=476&imagetools";
	import integration1Xl from "../assets/images/integration/integration1.png?webp&h=320&w=708&imagetools";
	import integration2 from "../assets/images/integration/integration2.png";
	import integration2Sm from "../assets/images/integration/integration2.png?webp&h=135&w=300&imagetools";
	import integration2Md from "../assets/images/integration/integration2.png?webp&h=160&w=348&imagetools";
	import integration2Lg from "../assets/images/integration/integration2.png?webp&h=215&w=476&imagetools";
	import integration2Xl from "../assets/images/integration/integration2.png?webp&h=320&w=708&imagetools";
	import integration3 from "../assets/images/integration/integration3.png";
	import integration3Sm from "../assets/images/integration/integration3.png?webp&h=320&w=608&imagetools";
	import integration3Md from "../assets/images/integration/integration3.png?webp&h=370&w=704&imagetools";
	import integration3Lg from "../assets/images/integration/integration3.png?webp&h=420&w=800&imagetools";
	import { nbspify } from "../../../core/utils/nbspify";
	import { getTranslate } from "../utils/getTranslate";
	import translations from "./Service.translations.json";
	import { getReferenceDetailPath } from "../../../core/schema/paths/getReferenceDetailPath";
	import Card from "../components/Card.svelte";
	import Breadcrumbs from "../components/Breadcrumbs.svelte";
	import type { SourceSetQuery } from "../components/Picture/SourceSetQuery.js";
	import { setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";

	type $$Props = ServiceProps;

	const { TENANT_LOCALE } = getGlobalSettings();
	const { relatedCaseStudies, texts, breadcrumbs } = $$restProps as $$Props;

	const translate = getTranslate(translations);

	type Image = Extract<(typeof texts)["content"][number], string[]>[number] | (typeof texts)["heroPhoto"];

	const imageMap = {
		"integration-hero": {
			thumbnail: integrationHero,
			"<md": integrationHeroSm,
			">md": integrationHeroMd,
			">lg": integrationHeroLg,
			">xl": integrationHeroXl,
		},
		integration1: {
			thumbnail: integration1,
			"<md": integration1Sm,
			">md": integration1Md,
			">lg": integration1Lg,
			">xl": integration1Xl,
		},
		integration2: {
			thumbnail: integration2,
			"<md": integration2Sm,
			">md": integration2Md,
			">lg": integration2Lg,
			">xl": integration2Xl,
		},
		integration3: {
			thumbnail: integration3,
			"<md": integration3Sm,
			">md": integration3Md,
			">lg": integration3Lg,
			">xl": integration3Lg,
		},
	} satisfies Record<
		Image,
		Partial<Record<SourceSetQuery, string>> & {
			thumbnail: string;
		}
	>;

	setContext(thumbnailImageContextKey, imageMap[texts.heroPhoto].thumbnail);
</script>

<Layout>
	{#if imageMap[texts.heroPhoto]}
		<section class="container">
			<div class="flex items-center overflow-hidden rounded-lg">
				<StaticPicture
					image={imageMap[texts.heroPhoto][">xl"]}
					alt={texts.headline[TENANT_LOCALE]}
					width={1424}
					height={420}
					loading="eager"
					imgClass="w-full object-cover"
					sourcesets={imageMap[texts.heroPhoto]}
				/>
			</div>
		</section>
	{/if}
	<Breadcrumbs {breadcrumbs} />
	<section class="container flex flex-col md:items-center">
		<div class="lg:w-[50rem]">
			<h1 class="m-0 mb-4 p-0 font-normal lg:mb-7 lg:leading-[3.25rem]">
				{nbspify(texts.headline[TENANT_LOCALE])}
			</h1>
			<h2 class="text-base font-normal lg:text-[1.5rem] lg:leading-8">{nbspify(texts.subheadline[TENANT_LOCALE])}</h2>
		</div>
		{#each texts.content as content}
			{#if "heading" in content && content.heading}
				<div class="mb-3 w-full lg:mb-5 lg:w-[50rem]">
					<h2 class="mb-0 text-start text-[1.5rem] font-medium uppercase lg:mt-4 lg:leading-7">
						{nbspify(content.heading[TENANT_LOCALE])}
					</h2>
				</div>
			{/if}
			{#if "paragraphs" in content}
				{#each content.paragraphs[TENANT_LOCALE] as paragraph}
					{#if "heading" in paragraph}
						<div class="w-full lg:w-[50rem]">
							<h3 class="mb-2 text-xs font-semibold uppercase lg:mb-[0.575rem]">{nbspify(paragraph.heading)}</h3>
						</div>
					{/if}
					<div class="lg:w-[50rem]">
						<p class="mb-3 text-xs lg:mb-[0.95rem] lg:leading-[1.4rem]">{nbspify(paragraph.text)}</p>
					</div>
				{/each}
			{:else if content.length === 2}
				<div class="flex justify-between gap-2">
					{#each content as image}
						<StaticPicture
							image={imageMap[image][">xl"]}
							alt="{texts.headline[TENANT_LOCALE]} - {translate('ilustration')}"
							width={708}
							height={320}
							loading="lazy"
							imgClass="w-full object-cover rounded-lg"
							class="my-[4rem]"
							sourcesets={imageMap[image]}
						/>
					{/each}
				</div>
			{:else}
				{#each content as image}
					<div class="lg:w-[50rem]">
						<StaticPicture
							image={imageMap[image][">xl"]}
							alt="{texts.headline[TENANT_LOCALE]} - {translate('ilustration')}"
							width={800}
							height={420}
							loading="lazy"
							imgClass="w-full object-cover rounded-lg"
							class="my-[3rem]"
							sourcesets={imageMap[image]}
						/>
					</div>
				{/each}
			{/if}
		{/each}
		{#if relatedCaseStudies.length > 0}
			<div class="mt-4 flex flex-col items-center gap-10 lg:mt-16">
				<h2 class="font-spaceGrotesk text-lg lg:text-xl">{translate("relatedCaseStudies")}</h2>
				<div
					class="gap-2 {relatedCaseStudies.length < 3
						? 'lg:grid-cols-2 xl:w-2/3'
						: 'grid-cols-1 lg:grid-cols-3'} {relatedCaseStudies.length < 2 ? 'flex justify-center' : 'grid'}"
				>
					{#each relatedCaseStudies as caseStudy}
						<Card
							title={caseStudy.title}
							description={caseStudy.perex}
							urlSlug={getReferenceDetailPath(caseStudy)}
							image={caseStudy.previewImage}
							squaredImage={relatedCaseStudies.length < 3}
						/>
					{/each}
				</div>
			</div>
		{/if}
	</section>
</Layout>
